import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LayoutHeader from '../../layout/LayoutHeader';
import { getCachedData, refreshData } from '../../utils/dataPreloader';

const WorksDetail = () => {
    const { songId } = useParams();
    const navigate = useNavigate();
    const cachedCatalog = useSelector((state) => state.dataCache?.catalog?.data);
    const [song, setSong] = useState(null);
    const [expandedSections, setExpandedSections] = useState({
        master: false,
        publishing: false,
        writers: true
    });

    useEffect(() => {
        const findSong = (catalogData) => {
            if (!catalogData || !Array.isArray(catalogData)) return null;
            return catalogData.find(item => item._id === songId) || null;
        };

        // Use cached data immediately if available
        if (cachedCatalog) {
            const foundSong = findSong(cachedCatalog);
            if (foundSong) {
                setSong(foundSong);
            }
        }
        
        // Refresh in background if needed
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
        if (token) {
            refreshData('catalog', 'user/getCatalog', token).then((data) => {
                if (data) {
                    const foundSong = findSong(data);
                    if (foundSong) {
                        setSong(foundSong);
            }
                }
            });
        }
    }, [songId, cachedCatalog]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatDuration = (duration) => {
        if (!duration) return '';
        if (typeof duration === 'string' && duration.includes(':')) {
            return duration;
        }
        return duration;
    };

    if (!song) {
        return (
            <LayoutHeader>
                <div className="flex justify-center w-full">
                    <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
                        <p className="text-white text-[12px] mb-4">Song not found</p>
                        <button
                            onClick={() => navigate('/catalog/works')}
                            className="text-[#17A2B8] text-[12px] hover:opacity-70"
                        >
                            Go back
                        </button>
                    </div>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Works</span>
                        <span 
                            className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                            onClick={() => navigate('/catalog/works')}
                        >
                            Go back
                        </span>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {/* Song Header */}
                        <div className="flex items-center gap-4 mb-6">
                            {/* Album Art */}
                            <div className="w-16 h-16 bg-[rgba(23,162,184,0.2)] rounded-lg flex items-center justify-center flex-shrink-0">
                                {song.album_artwork ? (
                                    <img 
                                        src={song.album_artwork} 
                                        alt={song.song_title || 'Album art'} 
                                        className="w-full h-full object-cover rounded-lg"
                                    />
                                ) : (
                                    <svg className="w-8 h-8 text-[#17A2B8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                    </svg>
                                )}
                            </div>

                            {/* Song Info */}
                            <div className="flex-1 min-w-0">
                                <h2 className="text-white text-[16px] font-normal mb-1">
                                    {song.song_title || 'Untitled'}
                                </h2>
                                <p className="text-white text-[12px] mb-1">
                                    {song.primary_artist || 'Unknown Artist'}
                                </p>
                                <div className="flex items-center gap-2 text-[rgba(255,255,255,0.7)] text-[11px]">
                                    {song.duration && (
                                        <span>{formatDuration(song.duration)}</span>
                                    )}
                                    {song.release_date && (
                                        <>
                                            {song.duration && <span>•</span>}
                                            <span>{formatDate(song.release_date)}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* MASTER Section */}
                        <div className="mb-4">
                            <div
                                onClick={() => toggleSection('master')}
                                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-2 cursor-pointer hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-white text-[12px] font-normal">MASTER</span>
                                    <svg 
                                        className={`w-4 h-4 text-[rgba(255,255,255,0.5)] transition-transform ${expandedSections.master ? 'rotate-90' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.master && song.master_contributors && song.master_contributors.length > 0 && (
                                <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
                                    <div className="space-y-3">
                                        {song.master_contributors.map((contributor, index) => (
                                            <div key={index} className="pb-3 border-b border-[rgba(255,255,255,0.05)] last:border-0 last:pb-0">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-white text-[12px] font-normal">{contributor.name || 'Unknown'}</span>
                                                    <span className="text-[rgba(255,255,255,0.7)] text-[11px]">{contributor.percentage || 0}%</span>
                                                </div>
                                                <div className="flex items-center gap-4 text-[rgba(255,255,255,0.5)] text-[10px]">
                                                    <span>{contributor.role || 'Writer'}</span>
                                                    {contributor.advance_fee && (
                                                        <span>Advance: ${contributor.advance_fee}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* PUBLISHING Section */}
                        <div className="mb-4">
                            <div
                                onClick={() => toggleSection('publishing')}
                                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-2 cursor-pointer hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-white text-[12px] font-normal">PUBLISHING</span>
                                    <svg 
                                        className={`w-4 h-4 text-[rgba(255,255,255,0.5)] transition-transform ${expandedSections.publishing ? 'rotate-90' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.publishing && song.publishing_contributors && song.publishing_contributors.length > 0 && (
                                <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
                                    <div className="space-y-3">
                                        {song.publishing_contributors.map((contributor, index) => (
                                            <div key={index} className="pb-3 border-b border-[rgba(255,255,255,0.05)] last:border-0 last:pb-0">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-white text-[12px] font-normal">{contributor.name || 'Unknown'}</span>
                                                    <span className="text-[rgba(255,255,255,0.7)] text-[11px]">{contributor.percentage || 0}%</span>
                                                </div>
                                                <div className="flex items-center gap-4 text-[rgba(255,255,255,0.5)] text-[10px]">
                                                    <span>{contributor.role || 'Writer'}</span>
                                                    {contributor.pro && (
                                                        <span>{contributor.pro}</span>
                                                    )}
                                                    {contributor.ipi_number && (
                                                        <span>IPI: {contributor.ipi_number}</span>
                                                    )}
                                                </div>
                                                {contributor.publishing_line && (
                                                    <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">{contributor.publishing_line}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* WRITERS & SPLITS Section */}
                        <div className="mb-4">
                            <div
                                onClick={() => toggleSection('writers')}
                                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-2 cursor-pointer hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-white text-[12px] font-normal">WRITERS & SPLITS</span>
                                    <svg 
                                        className={`w-4 h-4 text-[rgba(255,255,255,0.5)] transition-transform ${expandedSections.writers ? 'rotate-90' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.writers && (
                                <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-4">
                                    {/* Writers Table */}
                                    {song.master_contributors && song.master_contributors.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-white text-[11px] font-normal mb-3">Writer Roles and Ownership</h4>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-[10px]">
                                                    <thead>
                                                        <tr className="border-b border-[rgba(255,255,255,0.1)]">
                                                            <th className="text-left py-2 text-[rgba(255,255,255,0.7)] font-normal">Writer</th>
                                                            <th className="text-left py-2 text-[rgba(255,255,255,0.7)] font-normal">Role</th>
                                                            <th className="text-left py-2 text-[rgba(255,255,255,0.7)] font-normal">PRO</th>
                                                            <th className="text-right py-2 text-[rgba(255,255,255,0.7)] font-normal">Ownership %</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {song.master_contributors.map((contributor, index) => (
                                                            <tr key={index} className="border-b border-[rgba(255,255,255,0.05)]">
                                                                <td className="py-2 text-white">{contributor.name || 'Unknown'}</td>
                                                                <td className="py-2 text-[rgba(255,255,255,0.7)]">{contributor.role || 'Writer'}</td>
                                                                <td className="py-2 text-[rgba(255,255,255,0.7)]">-</td>
                                                                <td className="py-2 text-right text-[rgba(255,255,255,0.7)]">{contributor.percentage || 0}%</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Publishing Table */}
                                    {song.publishing_contributors && song.publishing_contributors.length > 0 && (
                                        <div>
                                            <h4 className="text-white text-[11px] font-normal mb-3">Writer PRO and IPI #</h4>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-[10px]">
                                                    <thead>
                                                        <tr className="border-b border-[rgba(255,255,255,0.1)]">
                                                            <th className="text-left py-2 text-[rgba(255,255,255,0.7)] font-normal">Writer</th>
                                                            <th className="text-left py-2 text-[rgba(255,255,255,0.7)] font-normal">PRO</th>
                                                            <th className="text-left py-2 text-[rgba(255,255,255,0.7)] font-normal">IPI #</th>
                                                            <th className="text-right py-2 text-[rgba(255,255,255,0.7)] font-normal">Ownership %</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {song.publishing_contributors.map((contributor, index) => (
                                                            <tr key={index} className="border-b border-[rgba(255,255,255,0.05)]">
                                                                <td className="py-2 text-white">{contributor.name || 'Unknown'}</td>
                                                                <td className="py-2 text-[rgba(255,255,255,0.7)]">{contributor.pro || '-'}</td>
                                                                <td className="py-2 text-[rgba(255,255,255,0.7)]">{contributor.ipi_number || '-'}</td>
                                                                <td className="py-2 text-right text-[rgba(255,255,255,0.7)]">{contributor.percentage || 0}%</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {(!song.master_contributors || song.master_contributors.length === 0) && 
                                     (!song.publishing_contributors || song.publishing_contributors.length === 0) && (
                                        <p className="text-[rgba(255,255,255,0.5)] text-[11px]">No writer information available</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default WorksDetail;

